import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { StackProvider, StackTheme, StackHandler } from '@stackframe/react';
import { stackClientApp } from './stack';
import { theme } from './theme/theme';
import { AuthProvider } from './hooks/useAuth';
import { NotificationProvider } from './hooks/useNotification';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import NotificationContainer from './components/NotificationContainer';
import TokenEntry from './pages/TokenEntry';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import SelectPages from './pages/SelectPages';
import Transactions from './pages/Transactions';
import Analytics from './pages/Analytics';
import Duplicates from './pages/Duplicates';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import ProcessingJobs from './pages/ProcessingJobs';
import ReviewJob from './pages/ReviewJob';
import Checks from './pages/Checks';
import FetchEmails from './pages/FetchEmails';
import GmailCallback from './pages/GmailCallback';
import SplitHub from './pages/SplitHub';
import EmailHub from './pages/EmailHub';
import Balances from './pages/Balances';
import Upcoming from './pages/Upcoming';

// StackHandler component for auth routing
function HandlerRoutes() {
  const location = useLocation();
  const isAccountSettings = location.pathname.includes('account-settings');
  
  return (
    <StackHandler 
      app={stackClientApp} 
      location={location.pathname} 
      fullPage={!isAccountSettings} 
    />
  );
}

function App() {
  return (
    <Suspense fallback={null}>
      <Router>
        <StackProvider app={stackClientApp}>
          <StackTheme>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <NotificationProvider>
                <AuthProvider>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/login" element={<TokenEntry />} />
                      
                      {/* Auth Handler Routes with Layout for account settings */}
                      <Route path="/handler/account-settings/*" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route path="*" element={<HandlerRoutes />} />
                      </Route>
                      
                      {/* Other Auth Handler Routes (fullPage) */}
                      <Route path="/handler/*" element={<HandlerRoutes />} />
                      
                      {/* Protected Routes */}
                      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route index element={<Navigate to="/dashboard" replace />} />
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="upload" element={<Upload />} />
                        <Route path="select-pages" element={<SelectPages />} />
                        <Route path="processing-jobs" element={<ProcessingJobs />} />
                        <Route path="review-job/:jobId" element={<ReviewJob />} />
                        <Route path="review" element={<ReviewJob />} />
                        <Route path="checks" element={<Checks />} />
                        <Route path="fetch-emails" element={<FetchEmails />} />
                        <Route path="gmail-callback" element={<GmailCallback />} />
                        <Route path="transactions" element={<Transactions />} />
                        <Route path="analytics" element={<Analytics />} />
                        <Route path="balances" element={<Balances />} />
                        <Route path="upcoming" element={<Upcoming />} />
                        <Route path="duplicates" element={<Duplicates />} />
                        <Route path="split" element={<SplitHub />} />
                        <Route path="email" element={<EmailHub />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="subscription" element={<Subscription />} />
                        <Route path="payment/success" element={<PaymentSuccess />} />
                        <Route path="payment/failed" element={<PaymentFailed />} />
                      </Route>
                      
                      {/* Fallback route */}
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    
                    {/* Global notification container */}
                    <NotificationContainer />
                  </Box>
                </AuthProvider>
              </NotificationProvider>
            </ThemeProvider>
          </StackTheme>
        </StackProvider>
      </Router>
    </Suspense>
  );
}

export default App;